import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from 'context/user-context';
import { GET_INSTALLATION_PREVIEW_WORKSPACE, GET_USER_ROLE } from 'gql/queries';
import { REFRESH_INSTALLATION_PREVIEW, RECORD_INSTALLATION_VERIFICATION } from 'gql/mutations';
import type { InstallationPreviewWorkspace } from 'types/installationPreview';
import type { InstallationVerificationBinding, InstallationVerificationCheck } from 'types/installationVerification';
import { installationPreviewDomain, installationPreviewPending, installationPreviewRequestKey, readInstallationPreviewWorkspace } from 'utils/installationPreview';
import { installationVerificationCheckKey, installationVerificationComplete, installationVerificationFingerprint, readInstallationVerificationChecks } from 'utils/installationVerification';
import { boundedInstallationRead, installationAutomaticReadDue, useVisibleInstallationRefresh } from './useVisibleInstallationRefresh';

const READ_ERROR = 'We could not load your installation preview. Try loading it again.';
const CONFIRM_ERROR = 'We could not confirm this preview request. Retry refresh to safely check the same request.';
const LONG_CAPTURE = 'The preview is taking longer than expected. Load the latest status to check again.';
const completionNotice = (workspace: InstallationPreviewWorkspace) => workspace.current?.snapshot?.surfaces.some(surface => surface.status === 'captured')
  ? workspace.installationStatus?.functionalVerification.status === 'verified'
    ? 'Your latest installation appearance was captured. Current functional check results are shown separately.'
    : 'Your latest installation appearance was captured. Cart behavior has not been verified.'
  : 'Preview check finished. No capture is available. Open your live store to review the installation.';

interface PreviewState {
  scope: string;
  workspace: InstallationPreviewWorkspace | null;
  loading: boolean;
  busy: boolean;
  error: string;
  notice: string;
  verificationBusy?: boolean;
  verificationError?: string;
  verificationNotice?: string;
  installationStatusUnavailable?: boolean;
}

function within<T>(promise: Promise<T>, duration = 30000): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('Preview request timed out')), duration);
    promise.then(result => { window.clearTimeout(timeout); resolve(result); }, error => { window.clearTimeout(timeout); reject(error); });
  });
}

export function useInstallationPreview(storeId: string, domain: string, canManage: boolean) {
  const client = useApolloClient();
  const { user } = useUser();
  const globalAdmin = user?.defaultRole === 'shipaid_admin';
  const scope = [user?.id || '', storeId, domain, canManage, globalAdmin].join('|');
  const active = useRef({ scope, client });
  active.current = { scope, client };
  const generation = useRef(0);
  const readSequence = useRef(0);
  const locked = useRef(false);
  const attempt = useRef<{ scope: string; key: string; acknowledgement: { id: string; revision: number; requestKey: string } | null } | null>(null);
  const verificationLocked = useRef(false);
  const verificationAttempt = useRef<{ scope: string; key: string; signature: string; binding: InstallationVerificationBinding } | null>(null);
  const poll = useRef<{ runId: string; deadline: number; stopped: boolean } | null>(null);
  const latestRead = useRef<{ scope: string; client: typeof client; controller: AbortController; promise: Promise<void> } | null>(null);
  const lastReadAttempt = useRef<{ scope: string; client: typeof client; at: number } | null>(null);
  const [visible, setVisible] = useState(document.visibilityState !== 'hidden');
  const [state, setState] = useState<PreviewState>({ scope, workspace: null, loading: true, busy: false, error: '', notice: '' });

  const read = useCallback(async (controller?: AbortController) => {
    if (controller?.signal.aborted) throw new Error('Preview read stopped');
    lastReadAttempt.current = { scope, client, at: Date.now() };
    const request = client.query({ query: GET_INSTALLATION_PREVIEW_WORKSPACE, variables: { input: { storeId } },
      fetchPolicy: 'no-cache', context: { queryDeduplication: false,
        ...(controller ? { fetchOptions: { signal: controller.signal } } : {}) } });
    const result = await (controller ? boundedInstallationRead(request, controller) : within(request));
    return readInstallationPreviewWorkspace(result.data?.installationPreviewWorkspace, storeId, domain);
  }, [client, storeId, domain, scope]);

  const loadLatest = useCallback((background: boolean) => {
    if (!user?.id || !storeId || !installationPreviewDomain(domain)) return;
    if (background && (locked.current || verificationLocked.current)) return;
    if (latestRead.current?.scope === scope && latestRead.current.client === client && background) return latestRead.current.promise;
    latestRead.current?.controller.abort(); latestRead.current = null;
    const controller = new AbortController();
    const epoch = generation.current;
    const sequence = ++readSequence.current;
    const current = () => active.current.scope === scope && active.current.client === client && generation.current === epoch && readSequence.current === sequence;
    setState(previous => ({ ...previous, scope, loading: true, error: '' }));
    const load = async () => {
      try {
        const workspace = await read(controller);
        if (!current()) return;
        setState(previous => {
          const before = previous.scope === scope ? previous.workspace?.current : null;
          const after = workspace.current;
          if (workspace.available && before && !after && Date.parse(before.expiresAt) > Date.now()) {
            return { ...previous, loading: false, error: READ_ERROR, notice: '', installationStatusUnavailable: true };
          }
          if (before && after && (before.id === after.id
            ? before.requestKey !== after.requestKey || after.revision < before.revision || (after.revision === before.revision && after.status !== before.status)
            : Date.parse(after.createdAt) < Date.parse(before.createdAt))) return { ...previous, loading: false, error: READ_ERROR, installationStatusUnavailable: true };
          return { ...previous, scope, workspace, loading: false, error: '', installationStatusUnavailable: false,
            notice: workspace.current?.status === 'failed' ? 'The latest preview could not be captured. You can refresh to try again.'
              : workspace.current?.status === 'completed' ? completionNotice(workspace) : after && before?.id === after.id ? previous.notice : '' };
        });
      } catch {
        if (current()) setState(previous => ({ ...previous, loading: false, error: READ_ERROR, installationStatusUnavailable: true }));
      }
    };
    const promise = load().finally(() => {
      if (latestRead.current?.controller === controller) latestRead.current = null;
    });
    latestRead.current = { scope, client, controller, promise };
    return promise;
  }, [client, domain, read, scope, storeId, user?.id]);
  const reload = useCallback(() => loadLatest(false), [loadLatest]);

  useEffect(() => {
    generation.current++;
    readSequence.current++;
    locked.current = false;
    attempt.current = null;
    verificationLocked.current = false;
    verificationAttempt.current = null;
    poll.current = null;
    latestRead.current?.controller.abort();
    latestRead.current = null;
    lastReadAttempt.current = null;
    setState({ scope, workspace: null, loading: true, busy: false, error: '', notice: '' });
    if (!user?.id || !storeId || !installationPreviewDomain(domain)) {
      setState({ scope, workspace: null, loading: false, busy: false, error: 'Select a connected Shopify store to view its installation preview.', notice: '' });
    } else if (document.visibilityState !== 'hidden') void loadLatest(true);
    // Numeric request fences must invalidate their latest values during cleanup.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generation.current++; readSequence.current++;
      latestRead.current?.controller.abort(); latestRead.current = null;
    };
  }, [domain, loadLatest, scope, storeId, user?.id]);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    const run = state.scope === scope ? state.workspace?.current : null;
    if (!run || !installationPreviewPending(run)) { poll.current = null; return; }
    if (!poll.current || poll.current.runId !== run.id) poll.current = { runId: run.id, deadline: Date.now() + 120000, stopped: false };
    const tracker = poll.current;
    if (tracker.stopped || !visible || state.busy) return;
    const remaining = tracker.deadline - Date.now();
    if (remaining <= 0) {
      tracker.stopped = true;
      setState(previous => ({ ...previous, notice: LONG_CAPTURE }));
      return;
    }
    const deadline = window.setTimeout(() => {
      if (active.current.scope !== scope || poll.current !== tracker) return;
      tracker.stopped = true;
      setState(previous => ({ ...previous, notice: LONG_CAPTURE }));
    }, remaining);
    const timer = window.setTimeout(() => {
      if (!tracker.stopped && document.visibilityState !== 'hidden' && active.current.scope === scope) void loadLatest(true);
    }, 3000);
    return () => { window.clearTimeout(timer); window.clearTimeout(deadline); };
  }, [loadLatest, scope, state.busy, state.scope, state.workspace, visible]);

  useVisibleInstallationRefresh(user?.id && storeId && installationPreviewDomain(domain) ? scope : null, () => {
    const last = lastReadAttempt.current;
    if (last?.scope === scope && last.client === client && !installationAutomaticReadDue(last.at)) return;
    void loadLatest(true);
  });

  const refresh = useCallback(async () => {
    if (locked.current || verificationLocked.current || !user?.id || !canManage || !storeId || !installationPreviewDomain(domain)) return;
    locked.current = true;
    latestRead.current?.controller.abort(); latestRead.current = null;
    const epoch = generation.current;
    ++readSequence.current;
    const current = () => active.current.scope === scope && active.current.client === client && generation.current === epoch;
    setState(previous => ({ ...previous, scope, busy: true, error: '', notice: '' }));
    try {
      const workspace = await read();
      if (!current()) return;
      const role = globalAdmin || await within(client.query({ query: GET_USER_ROLE, variables: { storeId, userId: user.id },
        fetchPolicy: 'no-cache', context: { queryDeduplication: false } })).then(result => Array.isArray(result.data?.storeRoles)
          && result.data.storeRoles.some((row: { role?: string }) => ['ADMIN', 'MANAGER'].includes(row.role || '')));
      if (!current()) return;
      if (!role) { setState(previous => ({ ...previous, error: 'Your store permission changed. An administrator or manager can refresh installation previews.' })); return; }
      if (!workspace.available) { setState(previous => ({ ...previous, workspace, error: 'Preview capture is not available for this store right now.' })); return; }
      if (installationPreviewPending(workspace.current) && workspace.current?.requestKey !== attempt.current?.key) {
        setState(previous => ({ ...previous, workspace, notice: 'A preview is already being captured. Showing its latest status.' }));
        return;
      }
      if (!attempt.current || attempt.current.scope !== scope) {
        const key = window.crypto?.randomUUID?.();
        if (!installationPreviewRequestKey(key)) throw new Error('A secure request key could not be created');
        attempt.current = { scope, key, acknowledgement: null };
      }
      const pending = attempt.current;
      const result = await within(client.mutate({ mutation: REFRESH_INSTALLATION_PREVIEW,
        variables: { input: { storeId, requestKey: pending.key } }, fetchPolicy: 'no-cache' }));
      if (!current()) return;
      const rawAcknowledgement = result.data?.refreshInstallationPreview;
      const acknowledgement = readInstallationPreviewWorkspace(rawAcknowledgement, storeId, domain);
      const acknowledged = acknowledgement.current;
      const coalesced = acknowledgement.reason === 'refresh_coalesced';
      const requestConfirmed = coalesced
        ? installationPreviewRequestKey(rawAcknowledgement?.requestedKey) && rawAcknowledgement.requestedKey === pending.key
        : acknowledged?.requestKey === pending.key;
      if (!acknowledgement.available || !acknowledged || !requestConfirmed
        || (pending.acknowledgement && (acknowledged.id !== pending.acknowledgement.id || acknowledged.requestKey !== pending.acknowledgement.requestKey))) throw new Error('Preview acknowledgement mismatch');
      pending.acknowledgement = { id: acknowledged.id, revision: acknowledged.revision, requestKey: acknowledged.requestKey };
      const verified = await read();
      if (!current()) return;
      const confirmed = verified.current;
      if (!confirmed || confirmed.id !== acknowledged.id || confirmed.requestKey !== acknowledged.requestKey || confirmed.revision < acknowledged.revision) throw new Error('Preview readback mismatch');
      if (coalesced) {
        setState(previous => ({ ...previous, workspace: verified, loading: false, error: '',
          notice: 'A recent preview request is still within its refresh window. Showing its latest status. Wait before requesting another capture.' }));
      } else if (confirmed.status === 'failed') {
        setState(previous => ({ ...previous, workspace: verified, error: 'The preview could not be captured. Refresh again to make a new attempt.', notice: '' }));
      } else {
        setState(previous => ({ ...previous, workspace: verified, loading: false, error: '',
          notice: confirmed.status === 'completed' ? completionNotice(verified) : 'Preview requested. Capturing your storefront appearance…' }));
      }
      attempt.current = null;
      poll.current = null;
    } catch {
      if (current()) setState(previous => ({ ...previous, error: CONFIRM_ERROR, notice: '' }));
    } finally {
      if (current()) {
        locked.current = false;
        setState(previous => ({ ...previous, busy: false, loading: false }));
      }
    }
  }, [canManage, client, domain, globalAdmin, read, scope, storeId, user?.id]);

  const recordVerification = useCallback(async (rawChecks: InstallationVerificationCheck[], binding: InstallationVerificationBinding): Promise<boolean> => {
    if (verificationLocked.current || locked.current || !globalAdmin || !canManage || !user?.id || !installationPreviewDomain(domain)) return false;
    verificationLocked.current = true;
    const epoch = generation.current;
    const sequence = ++readSequence.current;
    const activeScope = () => active.current.scope === scope && active.current.client === client && generation.current === epoch;
    const current = () => activeScope() && readSequence.current === sequence;
    const sortedChecks = (checks: InstallationVerificationCheck[]) => [...checks].sort((a, b) => installationVerificationCheckKey(a).localeCompare(installationVerificationCheckKey(b)));
    setState(previous => ({ ...previous, verificationBusy: true, verificationError: '', verificationNotice: '' }));
    try {
      const checks = readInstallationVerificationChecks(rawChecks);
      if (!binding || !/^[1-9]\d{0,19}$/.test(binding.themeId) || !installationVerificationFingerprint(binding.configurationFingerprint)
        || (!checks.some(check => check.result === 'failed') && !installationVerificationComplete(checks))) throw new Error('Invalid verification input');
      const fresh = await read();
      if (!current()) return false;
      const status = fresh.installationStatus;
      setState(previous => ({ ...previous, workspace: fresh, installationStatusUnavailable: false }));
      if (!status || status.themeId !== binding.themeId || status.configurationFingerprint !== binding.configurationFingerprint) {
        setState(previous => ({ ...previous, verificationError: 'The published theme or configuration changed. Recheck the storefront before recording results.' }));
        return false;
      }
      if (verificationAttempt.current && status.activation.status !== 'unavailable' && status.functionalVerification.status !== 'unavailable'
        && (verificationAttempt.current.binding.themeId !== status.themeId
          || verificationAttempt.current.binding.configurationFingerprint !== status.configurationFingerprint)) verificationAttempt.current = null;
      const signature = JSON.stringify([binding.themeId, binding.configurationFingerprint, sortedChecks(checks)]);
      if (verificationAttempt.current && verificationAttempt.current.signature !== signature) {
        setState(previous => ({ ...previous, verificationError: 'Retry unchanged results to confirm the previous request before changing the evidence.' }));
        return false;
      }
      if (!verificationAttempt.current) {
        const key = window.crypto?.randomUUID?.();
        if (!installationPreviewRequestKey(key)) throw new Error('A secure request key could not be created');
        verificationAttempt.current = { scope, key, signature, binding: { ...binding } };
      }
      const pending = verificationAttempt.current;
      const result = await within(client.mutate({ mutation: RECORD_INSTALLATION_VERIFICATION,
        variables: { input: { storeId, requestKey: pending.key, expectedThemeId: binding.themeId,
          expectedConfigurationFingerprint: binding.configurationFingerprint, checks } }, fetchPolicy: 'no-cache' }));
      if (!current()) return false;
      const raw = result.data?.recordInstallationVerification;
      const acknowledgement = readInstallationPreviewWorkspace(raw, storeId, domain);
      const evidence = acknowledgement.installationStatus?.functionalVerification.record;
      const matches = (workspace: InstallationPreviewWorkspace, id?: string) => {
        const installation = workspace.installationStatus;
        const recorded = installation?.functionalVerification.record;
        return !!recorded && (!id || recorded.id === id) && recorded.verifiedBy === user.id
          && recorded.themeId === binding.themeId && recorded.configurationFingerprint === binding.configurationFingerprint
          && installation?.themeId === binding.themeId && installation.configurationFingerprint === binding.configurationFingerprint
          && installation.functionalVerification.status === (checks.some(check => check.result === 'failed') ? 'failed' : 'verified')
          && JSON.stringify(sortedChecks(recorded.checks)) === JSON.stringify(sortedChecks(checks));
      };
      if (!evidence || raw.requestedKey !== pending.key || raw.recordedVerificationId !== evidence.id
        || !['verification_recorded', 'verification_replayed'].includes(raw.reason) || !matches(acknowledgement)) throw new Error('Verification acknowledgement mismatch');
      const confirmed = await read();
      if (!current()) return false;
      if (!matches(confirmed, evidence.id)) throw new Error('Verification readback mismatch');
      ++readSequence.current;
      verificationAttempt.current = null;
      setState(previous => ({ ...previous, workspace: confirmed, verificationError: '', verificationNotice: 'Functional checks recorded.' }));
      return true;
    } catch {
      if (activeScope()) {
        ++readSequence.current;
        setState(previous => ({ ...previous, loading: false, installationStatusUnavailable: true,
          verificationError: 'We could not confirm the functional check record. Retry unchanged results to safely confirm the same request.', verificationNotice: '' }));
      }
      return false;
    } finally {
      if (activeScope()) { verificationLocked.current = false; setState(previous => ({ ...previous, verificationBusy: false })); }
    }
  }, [canManage, client, domain, globalAdmin, read, scope, storeId, user?.id]);

  const scoped = state.scope === scope ? state : { scope, workspace: null, loading: true, busy: false, error: '', notice: '' };
  return { ...scoped, refresh, reload, recordVerification,
    canRecordVerification: globalAdmin && canManage && !scoped.installationStatusUnavailable && !!scoped.workspace?.installationStatus?.themeId
      && installationVerificationFingerprint(scoped.workspace?.installationStatus?.configurationFingerprint),
    recordingVerification: scoped.verificationBusy === true, verificationError: scoped.verificationError || '', verificationNotice: scoped.verificationNotice || '',
    installationStatusUnavailable: scoped.installationStatusUnavailable === true };
}
